import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/stores/authStore';
import { useConfigStore, configSelectors } from '@/lib/config';
import { authApi, ProjectInfo, LoginSuccessResponse } from '@/lib/api/auth';
import { getErrorMessage } from '@/lib/api/client';
import { LoginView } from '@/components/views/LoginView';
import { createComponentLogger } from '@/lib/utils/logger';
import { ROLE_CONFIG, type UserRole } from '@/lib/types/authorization';
import { loginSchema } from '@/lib/validations/auth';

const logger = createComponentLogger('LoginPage');

/**
 * LoginPage Component
 *
 * Provides user authentication interface with:
 * - Email/password form with validation
 * - Dynamic project/hospital selection (shown on same form when user has multiple projects)
 * - Error handling via toast notifications
 * - Loading states
 * - Automatic redirect after successful login
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, isLoading: authLoading, user, token } = useAuthStore();

  const config = useConfigStore(configSelectors.config);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // State for showing hospital selection when user has multiple projects
  const [availableProjects, setAvailableProjects] = React.useState<ProjectInfo[] | undefined>(
    undefined
  );

  // Redirect if already authenticated
  useEffect(() => {
    logger.log('Checking auth status - User:', user, 'Token:', token);

    if (user && token) {
      const redirectPath = user.role
        ? ROLE_CONFIG[user.role as UserRole]?.defaultRedirect || '/dashboard'
        : '/dashboard';
      logger.log('User authenticated, redirecting to', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [user, token, location.state, navigate]);

  /**
   * Handle form submission
   */
  const onSubmit = async (data: { email: string; password: string; projectReference?: string }) => {
    // Validate form data with Zod
    const validationResult = loginSchema.safeParse(data);
    if (!validationResult.success) {
      const errorMessage = validationResult.error.errors[0]?.message || 'Invalid input';
      toast.error(errorMessage, { position: 'top-left', duration: 3000 });
      return;
    }

    setIsSubmitting(true);

    try {
      logger.log('Starting login...');
      logger.log('Project selected:', data.projectReference || 'Auto-detect');

      // Call login API
      const response = await authApi.login({
        email: data.email,
        password: data.password,
        projectReference: data.projectReference,
      });

      // Check if project selection is required
      if (response.requiresProjectSelection) {
        logger.log('User has multiple projects, showing selection dropdown');
        setAvailableProjects(response.projects);
        // Show toast to inform user to select a hospital
        toast.info('Please select a hospital to continue', {
          position: 'top-left',
          duration: 4000,
        });
        setIsSubmitting(false);
        return;
      }

      // Successful login
      const successResponse = response as LoginSuccessResponse;
      logger.log('Login API response received:', successResponse.user.username);
      logger.log('projectConfig received:', successResponse.projectConfig);

      // Store auth data (refresh token is handled via HTTP-Only cookie)
      logger.log('Setting auth with user:', successResponse.user.username);
      setAuth(
        successResponse.user,
        successResponse.token,
        successResponse.projectConfig,
        successResponse.departmentKey,
        successResponse.departmentConfig
      );

      // Clear state
      setAvailableProjects(undefined);

      // Redirect based on user role
      const redirectPath = successResponse.user.role
        ? ROLE_CONFIG[successResponse.user.role as UserRole]?.defaultRedirect || '/dashboard'
        : '/dashboard';
      logger.log('Navigating to', redirectPath);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : getErrorMessage(error);
      logger.error('Login error:', errorMessage);
      // Show error using toast at top-left position
      toast.error(errorMessage, {
        position: 'top-left',
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting || authLoading;

  return (
    <LoginView
      branding={{
        appName: config.appName,
        appFullName: config.appFullName,
        appTagline: config.appTagline,
        version: config.version,
      }}
      labels={{
        title: config.labels.loginTitle,
        subtitle: config.labels.loginSubtitle,
      }}
      state={{ isLoading }}
      availableProjects={availableProjects}
      onSubmit={onSubmit}
    />
  );
}
